import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Scale } from "lucide-react";
import { useT } from "@/i18n";
import BrandMark from "@/components/BrandMark";

export default function ResetPassword() {
  const { t } = useT();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError(t("auth.mismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-lg p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <BrandMark className="h-7 w-7" iconClassName="h-6 w-6 text-amber-600" />
          <span className="font-serif text-xl font-semibold text-slate-900">RPLAW</span>
        </div>
        {!token ? (
          <div data-testid="reset-password-no-token">
            <h2 className="font-serif text-2xl text-slate-900">{t("auth.invalid_link")}</h2>
            <p className="text-sm text-slate-600 mt-3">{t("auth.missing_token")}</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-password-form">
            <h2 className="font-serif text-2xl text-slate-900">{t("auth.new_password")}</h2>
            <p className="text-sm text-slate-500 mt-1 mb-6">{t("auth.min_chars")}</p>
            {error && (
              <div data-testid="reset-password-error" className="mb-4 text-sm text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <Label htmlFor="rp-pass">{t("auth.new_password_label")}</Label>
                <Input
                  id="rp-pass"
                  data-testid="reset-password-input"
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="rp-confirm">{t("auth.confirm_password")}</Label>
                <Input
                  id="rp-confirm"
                  data-testid="reset-password-confirm-input"
                  type="password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1.5"
                />
              </div>
            </div>
            <Button
              data-testid="reset-password-submit-button"
              type="submit"
              disabled={loading}
              className="w-full mt-6 bg-slate-900 hover:bg-slate-800"
            >
              {loading ? t("common.saving") : t("auth.reset_btn")}
            </Button>
          </form>
        )}
        <div className="mt-4 text-center">
          <Link
            to="/login"
            data-testid="reset-back-to-login-link"
            className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2"
          >
            {t("auth.back_login")}
          </Link>
        </div>
      </div>
    </div>
  );
}
